import React, { useState, useMemo } from 'react';
import { TemporaryClosure } from '../services/storeHoursApiService.ts';
import {
  getUpcomingHolidays,
  filterNewHolidays,
  formatHolidayDate,
  getDaysUntil,
  getHolidayTypeLabel,
  Holiday
} from '../utils/holidayUtils.ts';
import './TemporaryClosuresManager.css';

interface TemporaryClosuresManagerProps {
  temporaryClosures: TemporaryClosure[];
  onChange: (closures: TemporaryClosure[]) => void;
  storeId: string;
}

interface ActivationModalState {
  isOpen: boolean;
  closure: TemporaryClosure | null;
  mode: 'closed' | 'special';
  specialOpen: string;
  specialClose: string;
}

const TemporaryClosuresManager: React.FC<TemporaryClosuresManagerProps> = ({
  temporaryClosures,
  onChange,
  storeId
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newClosure, setNewClosure] = useState({
    date: '',
    reason: '',
    closureType: 'closed' as 'closed' | 'special',
    specialOpen: '09:00',
    specialClose: '17:00'
  });

  // Activation modal state
  const [activationModal, setActivationModal] = useState<ActivationModalState>({
    isOpen: false,
    closure: null,
    mode: 'closed',
    specialOpen: '09:00',
    specialClose: '17:00'
  });

  // Auto-detect upcoming holidays not yet in the list
  const suggestedHolidays = useMemo(() => {
    const upcoming = getUpcomingHolidays(6);
    return filterNewHolidays(upcoming, temporaryClosures);
  }, [temporaryClosures]);

  const openActivationModal = (closure: TemporaryClosure) => {
    setActivationModal({
      isOpen: true,
      closure,
      mode: closure.specialHours ? 'special' : 'closed',
      specialOpen: closure.specialHours?.open || '09:00',
      specialClose: closure.specialHours?.close || '17:00'
    });
  };

  const handleActivate = () => {
    if (!activationModal.closure) return;

    const targetClosure = activationModal.closure;
    const updated = temporaryClosures.map(closure => {
      if (closure.date.getTime() === targetClosure.date.getTime() && closure.reason === targetClosure.reason) {
        if (activationModal.mode === 'special') {
          return {
            ...closure,
            approved: true,
            specialHours: {
              open: activationModal.specialOpen,
              close: activationModal.specialClose
            }
          };
        } else {
          // Remove special hours if switching to closed
          const { specialHours, ...rest } = closure;
          return { ...rest, approved: true };
        }
      }
      return closure;
    });

    onChange(updated);
    setActivationModal({ isOpen: false, closure: null, mode: 'closed', specialOpen: '09:00', specialClose: '17:00' });
  };

  const handleDeactivate = (targetClosure: TemporaryClosure) => {
    const updated = temporaryClosures.map(closure =>
      closure.date.getTime() === targetClosure.date.getTime() && closure.reason === targetClosure.reason
        ? { ...closure, approved: false }
        : closure
    );
    onChange(updated);
  };

  const handleUpdateSpecialHours = (targetClosure: TemporaryClosure, field: 'open' | 'close', value: string) => {
    const updated = temporaryClosures.map(closure => {
      if (closure.date.getTime() === targetClosure.date.getTime() && closure.reason === targetClosure.reason) {
        return {
          ...closure,
          specialHours: {
            open: field === 'open' ? value : (closure.specialHours?.open || '09:00'),
            close: field === 'close' ? value : (closure.specialHours?.close || '17:00')
          }
        };
      }
      return closure;
    });
    onChange(updated);
  };

  const handleReasonChange = (targetClosure: TemporaryClosure, newReason: string) => {
    const updated = temporaryClosures.map(closure =>
      closure.date.getTime() === targetClosure.date.getTime() && closure.reason === targetClosure.reason
        ? { ...closure, reason: newReason }
        : closure
    );
    onChange(updated);
  };

  /**
   * Parse a YYYY-MM-DD date string as local noon (not UTC midnight)
   * This prevents timezone issues where dates shift by a day
   */
  const parseDateStringAsLocal = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0); // noon local time
  };

  const handleDateChange = (targetClosure: TemporaryClosure, dateString: string) => {
    const updated = temporaryClosures.map(closure =>
      closure.date.getTime() === targetClosure.date.getTime() && closure.reason === targetClosure.reason
        ? { ...closure, date: parseDateStringAsLocal(dateString) }
        : closure
    );
    onChange(updated);
  };

  const handleDelete = (closureToDelete: TemporaryClosure) => {
    if (window.confirm('Remove this closure?')) {
      const updated = temporaryClosures.filter(closure => {
        return !(closure.date.getTime() === closureToDelete.date.getTime() &&
          closure.reason === closureToDelete.reason);
      });
      onChange(updated);
    }
  };

  const handleAddClosure = () => {
    if (!newClosure.date || !newClosure.reason.trim()) {
      alert('Please fill in both date and reason');
      return;
    }

    const closure: TemporaryClosure = {
      date: parseDateStringAsLocal(newClosure.date),
      reason: newClosure.reason.trim(),
      approved: false
    };

    onChange([...temporaryClosures, closure]);
    setNewClosure({
      date: '',
      reason: '',
      closureType: 'closed',
      specialOpen: '09:00',
      specialClose: '17:00'
    });
    setShowAddForm(false);
  };

  const handleAddHoliday = (holiday: Holiday) => {
    const closure: TemporaryClosure = {
      date: holiday.date,
      reason: holiday.name,
      approved: false
    };
    onChange([...temporaryClosures, closure]);
  };

  const handleAddAllHolidays = () => {
    const newClosures = suggestedHolidays.map(holiday => ({
      date: holiday.date,
      reason: holiday.name,
      approved: false
    }));
    onChange([...temporaryClosures, ...newClosures]);
  };

  const formatDateForInput = (date: Date): string => {
    // Use local date components to avoid UTC conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const formatTime12Hour = (time24: string): string => {
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const sortedClosures = [...temporaryClosures].sort((a, b) => a.date.getTime() - b.date.getTime());

  // Separate active and inactive closures for better UX
  const activeClosures = sortedClosures.filter(c => c.approved && !isPastDate(c.date));
  const inactiveClosures = sortedClosures.filter(c => !c.approved && !isPastDate(c.date));
  const pastClosures = sortedClosures.filter(c => isPastDate(c.date));

  return (
    <div className="temporary-closures-manager">
      <div className="closures-header">
        <h3>Holiday & Closure Management</h3>
        <p className="closures-description">
          Schedule store closures or special hours for holidays and events.
          <strong> Only active items will display to customers.</strong>
        </p>
      </div>

      {/* Activation Modal */}
      {activationModal.isOpen && activationModal.closure && (
        <div className="activation-modal-overlay" onClick={() => setActivationModal({ ...activationModal, isOpen: false })}>
          <div className="activation-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Activate: {activationModal.closure.reason}</h4>
            <p className="modal-date">{formatHolidayDate(activationModal.closure.date)}</p>

            <div className="activation-options">
              <label className={`activation-option ${activationModal.mode === 'closed' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="activationType"
                  checked={activationModal.mode === 'closed'}
                  onChange={() => setActivationModal({ ...activationModal, mode: 'closed' })}
                />
                <div className="option-content">
                  <span className="option-title">Closed All Day</span>
                  <span className="option-desc">Store will be completely closed</span>
                </div>
              </label>

              <label className={`activation-option ${activationModal.mode === 'special' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="activationType"
                  checked={activationModal.mode === 'special'}
                  onChange={() => setActivationModal({ ...activationModal, mode: 'special' })}
                />
                <div className="option-content">
                  <span className="option-title">Special Hours</span>
                  <span className="option-desc">Store open with modified hours</span>
                </div>
              </label>
            </div>

            {activationModal.mode === 'special' && (
              <div className="special-hours-inputs">
                <div className="time-field">
                  <label>Open</label>
                  <input
                    type="time"
                    value={activationModal.specialOpen}
                    onChange={(e) => setActivationModal({ ...activationModal, specialOpen: e.target.value })}
                  />
                </div>
                <span className="time-separator">to</span>
                <div className="time-field">
                  <label>Close</label>
                  <input
                    type="time"
                    value={activationModal.specialClose}
                    onChange={(e) => setActivationModal({ ...activationModal, specialClose: e.target.value })}
                  />
                </div>
              </div>
            )}

            <div className="modal-preview">
              Customers will see:{' '}
              <strong>
                {activationModal.mode === 'closed'
                  ? `"Closed (${activationModal.closure.reason})"`
                  : `"${activationModal.closure.reason}: ${formatTime12Hour(activationModal.specialOpen)} - ${formatTime12Hour(activationModal.specialClose)}"`
                }
              </strong>
            </div>

            <div className="modal-actions">
              <button
                className="modal-cancel-btn"
                onClick={() => setActivationModal({ ...activationModal, isOpen: false })}
              >
                Cancel
              </button>
              <button
                className="modal-activate-btn"
                onClick={handleActivate}
              >
                Activate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suggested Holidays - Auto-detected upcoming holidays */}
      {suggestedHolidays.length > 0 && (
        <div className="suggested-holidays-section">
          <div className="suggested-header">
            <h4>Upcoming Holidays</h4>
            <button
              onClick={handleAddAllHolidays}
              className="add-all-btn"
            >
              Add All
            </button>
          </div>
          <p className="suggested-subtitle">
            Click to add holidays to your schedule. They'll be inactive until you activate them.
          </p>
          <div className="suggested-holidays-list">
            {suggestedHolidays.map((holiday) => {
              const daysUntil = getDaysUntil(holiday.date);
              return (
                <button
                  key={holiday.date.toISOString()}
                  className="suggested-holiday-chip"
                  onClick={() => handleAddHoliday(holiday)}
                  title={`Add ${holiday.name}`}
                >
                  <span className="chip-name">{holiday.name}</span>
                  <span className="chip-date">{formatHolidayDate(holiday.date)}</span>
                  <span className={`chip-type ${holiday.type}`}>{getHolidayTypeLabel(holiday.type)}</span>
                  {daysUntil <= 14 && (
                    <span className="chip-urgent">
                      {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil}d`}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Active Closures */}
      {activeClosures.length > 0 && (
        <div className="closures-section">
          <div className="section-header">
            <h4 className="section-title">
              <span className="status-dot active"></span>
              Active
            </h4>
            <span className="section-count">{activeClosures.length}</span>
          </div>
          <p className="section-subtitle">Visible to customers</p>
          <div className="closures-list">
            {activeClosures.map((closure) => (
              <div key={`${closure.date.getTime()}-${closure.reason}`} className={`closure-card active ${closure.specialHours ? 'special-hours' : ''}`}>
                <div className="closure-card-main">
                  <div className="closure-info">
                    <input
                      type="date"
                      value={formatDateForInput(closure.date)}
                      onChange={(e) => handleDateChange(closure, e.target.value)}
                      className="closure-date-input"
                    />
                    <input
                      type="text"
                      value={closure.reason}
                      onChange={(e) => handleReasonChange(closure, e.target.value)}
                      className="closure-reason-input"
                      placeholder="Reason"
                    />
                    {closure.specialHours ? (
                      <span className="hours-badge special">
                        {formatTime12Hour(closure.specialHours.open)} - {formatTime12Hour(closure.specialHours.close)}
                      </span>
                    ) : (
                      <span className="hours-badge closed">Closed</span>
                    )}
                  </div>
                  <div className="closure-actions">
                    <button
                      onClick={() => handleDeactivate(closure)}
                      className="status-toggle active"
                      title="Click to deactivate"
                    >
                      Active
                    </button>
                    <button
                      onClick={() => handleDelete(closure)}
                      className="delete-btn"
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                {closure.specialHours && (
                  <div className="special-hours-edit">
                    <label>Hours:</label>
                    <input
                      type="time"
                      value={closure.specialHours.open}
                      onChange={(e) => handleUpdateSpecialHours(closure, 'open', e.target.value)}
                      className="time-input"
                    />
                    <span>to</span>
                    <input
                      type="time"
                      value={closure.specialHours.close}
                      onChange={(e) => handleUpdateSpecialHours(closure, 'close', e.target.value)}
                      className="time-input"
                    />
                  </div>
                )}
                <div className="closure-preview">
                  Customers will see:{' '}
                  <strong>
                    {closure.specialHours
                      ? `"${closure.reason}: ${formatTime12Hour(closure.specialHours.open)} - ${formatTime12Hour(closure.specialHours.close)}"`
                      : `"Closed (${closure.reason})"`
                    }
                  </strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inactive Closures */}
      {inactiveClosures.length > 0 && (
        <div className="closures-section">
          <div className="section-header">
            <h4 className="section-title">
              <span className="status-dot inactive"></span>
              Scheduled (Inactive)
            </h4>
            <span className="section-count">{inactiveClosures.length}</span>
          </div>
          <p className="section-subtitle">Not visible to customers until activated</p>
          <div className="closures-list">
            {inactiveClosures.map((closure) => {
              const daysUntil = getDaysUntil(closure.date);
              const isUrgent = daysUntil <= 7 && daysUntil > 0;

              return (
                <div
                  key={`${closure.date.getTime()}-${closure.reason}`}
                  className={`closure-card inactive ${isUrgent ? 'urgent' : ''}`}
                >
                  <div className="closure-card-main">
                    <div className="closure-info">
                      <input
                        type="date"
                        value={formatDateForInput(closure.date)}
                        onChange={(e) => handleDateChange(closure, e.target.value)}
                        className="closure-date-input"
                      />
                      <input
                        type="text"
                        value={closure.reason}
                        onChange={(e) => handleReasonChange(closure, e.target.value)}
                        className="closure-reason-input"
                        placeholder="Reason"
                      />
                      {isUrgent && (
                        <span className="urgent-badge">
                          {daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}
                        </span>
                      )}
                    </div>
                    <div className="closure-actions">
                      <button
                        onClick={() => openActivationModal(closure)}
                        className="status-toggle inactive"
                        title="Click to activate"
                      >
                        Activate
                      </button>
                      <button
                        onClick={() => handleDelete(closure)}
                        className="delete-btn"
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  {isUrgent && (
                    <div className="urgent-warning">
                      This date is coming up soon. Activate now to notify customers.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Past Closures (collapsed) */}
      {pastClosures.length > 0 && (
        <div className="closures-section past-section">
          <details>
            <summary className="past-summary">
              <span className="status-dot past"></span>
              Past Closures ({pastClosures.length})
            </summary>
            <div className="closures-list">
              {pastClosures.map((closure) => (
                <div key={`${closure.date.getTime()}-${closure.reason}`} className="closure-card past">
                  <div className="closure-card-main">
                    <div className="closure-info">
                      <span className="closure-date-display">
                        {formatHolidayDate(closure.date)}
                      </span>
                      <span className="closure-reason-display">{closure.reason}</span>
                      <span className={`past-status ${closure.approved ? 'was-active' : 'was-inactive'}`}>
                        {closure.approved
                          ? (closure.specialHours ? `Was open ${formatTime12Hour(closure.specialHours.open)}-${formatTime12Hour(closure.specialHours.close)}` : 'Was closed')
                          : 'Was inactive'
                        }
                      </span>
                    </div>
                    <button
                      onClick={() => handleDelete(closure)}
                      className="delete-btn"
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* Empty state */}
      {sortedClosures.length === 0 && suggestedHolidays.length === 0 && (
        <div className="empty-state">
          <p>No closures scheduled</p>
        </div>
      )}

      {/* Add Custom Closure */}
      <div className="add-closure-section">
        {!showAddForm ? (
          <button
            onClick={() => setShowAddForm(true)}
            className="add-custom-btn"
          >
            + Add Custom Closure
          </button>
        ) : (
          <div className="add-closure-form">
            <h4>Add Custom Closure</h4>
            <div className="form-row">
              <div className="form-field">
                <label>Date</label>
                <input
                  type="date"
                  value={newClosure.date}
                  onChange={(e) => setNewClosure({ ...newClosure, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-field flex-grow">
                <label>Reason (shown to customers)</label>
                <input
                  type="text"
                  value={newClosure.reason}
                  onChange={(e) => setNewClosure({ ...newClosure, reason: e.target.value })}
                  placeholder="e.g., Staff Training"
                />
              </div>
            </div>
            <div className="form-actions">
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewClosure({
                    date: '',
                    reason: '',
                    closureType: 'closed',
                    specialOpen: '09:00',
                    specialClose: '17:00'
                  });
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleAddClosure}
                className="save-btn"
              >
                Add Closure
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemporaryClosuresManager;
